import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";

const GsaChallenge1Bounty = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [challenge, setChallenge] = useState("");
  const [bountyAmount, setBountyAmount] = useState("7500");
  const [timeline, setTimeline] = useState("45");
  const [requirements, setRequirements] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    if (location.state) {
      setSelectedAreas(location.state.selectedAreas || []);
      setChallenge(location.state.challenge || "");
      setRequirements(`Solution Requirements for GSA Federal Office Renovation Timeline Reduction:

OBJECTIVE: Reduce average design-to-occupancy timeline by 20% for federal office renovations

DELIVERABLES:
• Detailed implementation strategy with timeline analysis
• Process optimization recommendations for selected functional areas: ${location.state.selectedAreas?.join(', ') || ''}
• ROI analysis demonstrating 20% timeline reduction
• Risk mitigation plan for accelerated schedules
• Pilot program proposal for 2-3 federal facilities

COMPLIANCE REQUIREMENTS:
• All solutions must comply with GSA P100 Facilities Standards
• Maintain adherence to federal accessibility requirements (Section 508)
• Ensure compliance with NEHRP seismic standards where applicable
• Follow FAR procurement guidelines for contractor selection

ACCEPTANCE CRITERIA:
• Demonstrate measurable 20% reduction in design-to-occupancy timeline
• Maintain or improve quality standards
• Compatible with existing GSA project management systems
• Scalable across multiple federal office facilities
• Include change management plan for PBS staff adoption

SUPPORTING KNOWLEDGE BASES:
• Fast Track Scheduling Templates
• Modular Phasing Logic  
• Common Bottlenecks in GSA Buildouts`);
    }
  }, [location]);

  const handleSubmit = () => {
    if (!acceptTerms) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms and conditions to proceed.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "GSA Challenge #1 Call for Solutions Published",
      description: "Your challenge has been posted to the federal knowledge network.",
    });
    
    navigate("/gsa-challenge-1/bounty-details", { 
      state: { 
        challenge,
        selectedAreas,
        bountyAmount,
        timeline,
        requirements
      }
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
            alt="iSPAI Logo" 
            className="h-16 mx-auto mb-6 cursor-pointer"
            onClick={() => navigate("/gsa-channel")}
          />
          <h1 className="text-3xl font-bold mb-4">Create GSA Challenge #1 Bounty</h1>
        </div>

        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-xl text-blue-800">Challenge Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-blue-900 font-medium mb-4">{challenge}</p>
            <div>
              <p className="text-sm font-semibold text-blue-700 mb-2">Selected Functional Areas:</p>
              <div className="flex flex-wrap gap-2">
                {selectedAreas.map((area, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-blue-200 text-blue-800 text-sm rounded-full"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Bounty Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="bountyAmount" className="text-base font-medium">Token Reward Amount</Label>
                <Input 
                  id="bountyAmount"
                  type="number"
                  value={bountyAmount}
                  onChange={(e) => setBountyAmount(e.target.value)}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Recommended: 7,500 tokens for complex GSA challenges</p>
              </div>
              
              <div>
                <Label htmlFor="timeline" className="text-base font-medium">Timeline (Days)</Label>
                <Input 
                  id="timeline"
                  type="number"
                  value={timeline}
                  onChange={(e) => setTimeline(e.target.value)}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">45 days recommended for comprehensive solutions</p>
              </div>
            </div>

            <div>
              <Label htmlFor="requirements" className="text-base font-medium">Detailed Requirements</Label>
              <Textarea
                id="requirements"
                value={requirements}
                onChange={(e) => setRequirements(e.target.value)}
                className="mt-1 min-h-40"
                placeholder="Specify requirements, deliverables, and acceptance criteria..."
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8 border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">GSA Federal Compliance</h3>
            <p className="text-yellow-700 text-sm">
              This challenge follows GSA P100 Facilities Standards and federal acquisition regulations. 
              All solutions must maintain compliance with accessibility, security, and sustainability requirements.
            </p>
          </CardContent>
        </Card>

        <div className="flex items-start space-x-2 mb-8">
          <Checkbox 
            id="terms" 
            checked={acceptTerms}
            onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
          />
          <div>
            <Label htmlFor="terms" className="cursor-pointer text-sm">
              I confirm this challenge aligns with GSA priorities and federal regulations
            </Label>
            <p className="text-gray-500 text-xs mt-1">
              By submitting, you confirm this challenge addresses real GSA needs and will be evaluated by 
              federal domain experts including Julia De Rosi, Charles Edwards, and Heather Edwards.
            </p>
          </div>
        </div>

        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={() => navigate("/gsa-challenge-1/functional-area")}
          >
            Back to Functional Areas
          </Button>
          
          <Button 
            onClick={handleSubmit}
            disabled={!acceptTerms}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2"
          >
            Publish GSA Challenge #1
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GsaChallenge1Bounty;